import { reportError } from "utils/diagnostics";
import AddCircleOutlined from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { Menu, MenuItem } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import TableRow from "@mui/material/TableRow";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  addSingleDeviceAPIHelper,
  bulkUpdateDeviceSoftware,
  bulkPushDeviceConfig,
  changeDeviceStatusFromUI,
  deleteDevice,
  getDeviceDirectory,
} from "api/DeviceHelpers";
import { NxtIconButton } from "components/common/nxt-icon-button/nxt-icon-button.component";
import { Popup } from "components/common/popup/popup.component";
import { Spinner } from "components/common/spinner/spinner.component";
import { PageTitle } from "components/common/Text";

import { getExpirationDate, onEnter, pollUntil } from "utils/general";
import { ActionBar } from "components/common/Wrapper";
import { exportCsv } from "components/utilites/utilities";
import { getAllDevicePools } from "api/DevicePoolHelpers";
import CustomTable from "components/table/table.component";
import { CommonSelect } from "components/common/common-select/common-select.component";
import { FieldError, StyledTextField } from "components/common/Input";
import {
  COLUMN_TYPE,
  MAX_DEVICES_PER_ORG,
  REFRESH_VALUES_POLL_INTERVAL,
  REFRESH_VALUES_POLL_TIMEOUT,
} from "utils/constants";
import { TableBodyCell } from "components/table/table.style";
import TableFilterNew from "components/table/TableFilterNew.component";
import { ButtonText } from "components/common/Buttons";
import useConfirm from "hooks/useConfirm";
import { DevicePoolTypes } from "pages/Pools/pools.config";
import { findPoolByName, getOrgDeviceCount } from "pages/Pools/pools.utilities";
import { useRequest } from "hooks/useRequest";
import { showInfo, showSuccess, showWarning, showError } from "utils/notifications";
import { getDateString, getFilteredListNew, formatDateOrDash } from "utils/util-functions";
import DASHBOARD_COLUMNS, {
  DeviceColumnTypes,
  DeviceValueTypes,
  eiInterfaceTypes,
  READ_ONLY_DASHBOARD_COLUMNS,
} from "./devices.config";
import exportFileIcon from "../../assets/svg/file_export_24dp_BLUE.svg";
import style from "./devices.page.module.css";
import { useAuth } from "hooks/useProvideAuth";
import { getTableCellValue } from "./utilities/device.utilities";
import { BoldText } from "components/common/text/text.component";
import { UpdateSoftwareDialog } from "components/common/update-software-dialog/update-software-dialog.component";
import { PushConfigDialog } from "components/common/push-config-dialog/push-config-dialog.component";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { buildDeviceDirectoryParams, buildDeviceDirectoryPath } from "./device-directory";

const RELEASE_NOTES_URL = "https://airgain.com/products/megafi-2-hpue/";

const normalizeDeviceData = (values: DeviceValueTypes[] = []): DeviceValueTypes[] =>
  values.map((value: DeviceValueTypes) => {
    const wan = value.eiInterfaces?.find((item: eiInterfaceTypes) => item.type === "WAN");

    return {
      ...value,
      txBytes: wan?.txBytes ?? 0,
      rxBytes: wan?.rxBytes ?? 0,
    };
  });

export default function DevicesPage(): JSX.Element {
  const navigate = useNavigate();

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [isOpenNewDeviceModal, setIsOpenNewDeviceModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [poolList, setPoolList] = useState<DevicePoolTypes[]>([]);
  const [filterColumn, setFilterColumn] = useState<string>("");
  const [filterWord, setFilterWord] = useState<any>("");
  const [filterType, setFilterType] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("asc");
  const [updating, setUpdating] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>("eiSerialNumber");
  const [expirationDate, setExpirationDate] = useState<string>("");
  const [isCreatingNewDevice, setIsCreatingNewDevice] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("Creating new device...");
  const [isOpenUpdateDialog, setIsOpenUpdateDialog] = useState<boolean>(false);
  const [isUpdatingSoftware, setIsUpdatingSoftware] = useState<boolean>(false);
  const [isOpenConfigDialog, setIsOpenConfigDialog] = useState<boolean>(false);
  const [isPushingConfig, setIsPushingConfig] = useState<boolean>(false);

  const { isReadOnly, isDemoUser, sub }: Record<string, string> = useAuth();
  const getExpire = async () => {
    if (!sub) return;

    const response = await getExpirationDate(sub);
    setExpirationDate(response);
  };

  useEffect(() => {
    getExpire();
  }, [sub]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  }: FieldValues = useForm();

  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(actionsAnchorEl);
  const handleActionsClick = (event: { currentTarget: HTMLElement }) => {
    setActionsAnchorEl(event.currentTarget);
  };
  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const [getConfirmation, Confirmation] = useConfirm();

  const [isRefreshPolling, setIsRefreshPolling] = useState<boolean>(false);

  const isMountedRef = useRef<boolean>(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const filterColumns = DASHBOARD_COLUMNS.filter(({ filter }) => filter);
  const dataColumns = useMemo(() => {
    const columns = [...DASHBOARD_COLUMNS];
    columns.splice(0, 1);
    return columns;
  }, []);

  const poolOptions = useMemo(
    () => poolList.map(({ devicePool }) => ({ value: devicePool, label: devicePool })),
    [poolList],
  );

  const directoryQuery = useMemo(
    () => ({
      page,
      pageSize: rowsPerPage,
      sortBy,
      sortOrder,
      filterColumn: filterColumn || undefined,
      filterType: filterType || undefined,
      filterValue: filterColumn ? filterWord : undefined,
    }),
    [filterColumn, filterType, filterWord, page, rowsPerPage, sortBy, sortOrder],
  );

  const { data, isLoading, mutate } = useRequest(buildDeviceDirectoryPath(directoryQuery));

  const deviceData = useMemo(() => normalizeDeviceData(data?.deviceData), [data]);
  const totalDeviceCount = data?.totalDeviceCount ?? 0;

  const getDeviceDataCsv = async () => {
    const currentTime = new Date();
    const formattedTime = getDateString(currentTime);
    const fileName = `HPUE_Device_List_${formattedTime}.csv`;

    const combinedLabels = [
      "Device Alias",
      "Serial Number",
      "IMEI",
      "MAC",
      "SIM Phone Number",
      "APN",
      "Device Pool",
      "Software Version",
      "Device Description",
      "Last Check-In",
      "Online Status",
      "Location Tracking",
    ];

    try {
      const exportResponse = await getDeviceDirectory(
        buildDeviceDirectoryParams({ ...directoryQuery, page: 0, all: true }),
      );
      const dataPoints = normalizeDeviceData(exportResponse?.deviceData).map((device: DeviceValueTypes) => ({
        deviceAlias: device.deviceAlias,
        serialNumber: device.eiSerialNumber,
        imei: device.imei,
        mac: device.mac,
        phoneNumber: device.phoneNumber,
        apn: device.apn,
        devicePool: device.devicePool,
        eiSoftwareVersion: device.eiSoftwareVersion,
        deviceDescription: device.deviceDescription,
        lastCheckIn: formatDateOrDash(device.lastCheckIn, "YYYY/MM/DD HH:mm:ss"),
        online: device.online ? "Online" : "Offline",
        locationTrailEnabled: device.locationTrailEnabled ? "Enabled" : "Disabled",
      }));

      exportCsv(dataPoints, combinedLabels, fileName);
    } catch (error) {
      reportError("devices.getDeviceDataCsv", error);
      showError("Failed to export devices.");
    }
  };

  const fetchData = () => {
    setSelectedRows([]);
    mutate?.();
  };

  // TODO HERE

  const getPools = async () => {
    const role = localStorage.getItem("roleName");

    if (role === "demouser") {
      return;
    }

    await getAllDevicePools().then(({ data: devicePools }) => {
      if (devicePools?.length > 0) {
        setPoolList(devicePools);
      }
    });
  };

  useEffect(() => {
    // load device pools
    getPools();
  }, [isDemoUser]);

  const getRowSN = (row: DeviceValueTypes) => (row.eiSerialNumber === undefined ? "default" : row.eiSerialNumber);

  const handleEIRowClick = (serialNumber: string) => {
    if (isReadOnly) {
      return;
    }

    if (serialNumber !== "default") {
      navigate(`/devices/${serialNumber}`);
    }
  };

  const onNewDevice = () => {
    setValue("serialNumber", "");
    setValue("macAddress", "");
    setIsOpenNewDeviceModal(true);
  };

  const onRemoveDevice = async () => {
    if (selectedRows && selectedRows.length > 0) {
      const isConfirm = await getConfirmation({
        title: "Delete Device?",
        content: "Are you sure to delete selected devices?",
        buttonTxt: "Delete",
      });

      if (isConfirm) {
        if (selectedRows.length > 1) {
          showWarning("Cannot remove multiple devices for now, please select single device!");
        } else {
          try {
            await deleteDevice(selectedRows[0]);
            fetchData();
            showSuccess(`Successfully removed device: ${selectedRows[0]}`);
          } catch (error) {
            reportError("pages/Devices/devices.page.onRemoveDevice", error);
            // error handling
          }
        }
      }
    }
  };

  const onModalClose = (event: never, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason !== "backdropClick") {
      setIsOpenNewDeviceModal(false);
    }
  };

  const onAddNewDevice = async ({ macAddress, serialNumber, devicePool }: FieldValues): Promise<void> => {
    const targetPoolName = devicePool || "default";
    const targetPool = findPoolByName(poolList, targetPoolName);
    if (targetPool) {
      const orgTotal = getOrgDeviceCount(poolList, targetPool.orgID);
      if (orgTotal + 1 > MAX_DEVICES_PER_ORG) {
        showError(`Cannot add device: organization already has ${orgTotal} of ${MAX_DEVICES_PER_ORG} devices.`);
        return;
      }
    }

    setIsCreatingNewDevice(true);
    setModalMessage("Creating new device...");

    try {
      const res = await addSingleDeviceAPIHelper({
        mac: macAddress,
        serialNumber,
        devicePool: devicePool || "default",
      });

      if (!res) {
        return;
      }

      if (res.data) {
        showSuccess("Added new device successfully!");
        setIsOpenNewDeviceModal(false);
        // refresh table
        fetchData();
      }
    } catch (error) {
      reportError("devices.onAddNewDevice", error);
      // error handling
    } finally {
      setIsCreatingNewDevice(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, row: DeviceValueTypes) => {
    const { eiSerialNumber } = row;

    if (!eiSerialNumber) {
      return;
    }

    const newSelected = [...selectedRows];
    const index = selectedRows.indexOf(eiSerialNumber);

    if (event.target.checked) {
      if (index === -1) {
        newSelected.push(eiSerialNumber);
      }
    } else if (index !== -1) {
      newSelected.splice(index, 1);
    }

    setSelectedRows(newSelected);
  };

  const isSelectedRow = useCallback(
    (eiSerialNumber: string | undefined) => {
      if (!eiSerialNumber) {
        return false;
      }
      return selectedRows.indexOf(eiSerialNumber) !== -1;
    },
    [selectedRows],
  );

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allSerialNumbers = deviceData
        .map((row: DeviceValueTypes) => row.eiSerialNumber)
        .filter(Boolean) as string[];
      setSelectedRows(allSerialNumbers);
    } else {
      setSelectedRows([]);
    }
  };

  const isAllSelected = useMemo(() => {
    if (deviceData.length === 0) return false;
    const allSerialNumbers = deviceData.map((row: DeviceValueTypes) => row.eiSerialNumber).filter(Boolean) as string[];
    return (
      allSerialNumbers.length > 0 && allSerialNumbers.every((serialNumber) => selectedRows.indexOf(serialNumber) !== -1)
    );
  }, [deviceData, selectedRows]);

  const isIndeterminate = useMemo(() => {
    return selectedRows.length > 0 && !isAllSelected;
  }, [selectedRows, isAllSelected]);

  const handleRequestSort = (order: string, orderBy: string) => {
    setSortOrder(order);
    setSortBy(orderBy);
    setPage(0);
    setSelectedRows([]);
  };

  const onSearch = (column: string, columnType: string, searchContent: any) => {
    setFilterColumn(column);
    setFilterWord(searchContent);
    setFilterType(columnType);
    setPage(0);
    setSelectedRows([]);
  };

  const onResetFilter = () => {
    setFilterColumn("");
    setFilterWord("");
    setFilterType("");
    setPage(0);
    setSelectedRows([]);
  };

  const handleUpdateDevice = async (isRefresh: boolean) => {
    handleActionsClose();

    const isOnlineConfirmed = await getConfirmation({
      title: isRefresh ? "Refresh Values" : "Reboot Device",
      content: `This action will be applied to online devices only`,
      buttonTxt: ButtonText.Continue,
    });

    if (!isOnlineConfirmed) {
      return;
    }

    const deviceList = getFilteredListNew(deviceData, "online", "option", true);
    const rows = selectedRows.filter((eiSerialNumber) =>
      deviceList.find((x: { eiSerialNumber: string }) => x.eiSerialNumber === eiSerialNumber),
    );

    if (!rows.length) {
      showWarning("There are no devices online.");
      return;
    }
    const isRefreshValues = await getConfirmation({
      title: isRefresh ? "Refresh Values" : "Reboot Device",
      content: isRefresh
        ? "Are you sure to refresh values?"
        : "Are you sure? You will lose connectivity while the device reboots.",
      buttonTxt: ButtonText.Refresh,
    });

    if (!isRefreshValues) {
      return;
    }

    setUpdating(true);

    let lastCheckInBySerial = new Map<string | undefined, string>();
    try {
      if (isRefresh) {
        const current: any = await mutate?.();
        const baselineDevices = current?.deviceData ?? deviceList;
        lastCheckInBySerial = new Map<string | undefined, string>(
          baselineDevices.map((d: DeviceValueTypes): [string | undefined, string] => [d.eiSerialNumber, d.lastCheckIn]),
        );
      }

      await Promise.all(
        rows.map((r) =>
          changeDeviceStatusFromUI({
            eiSerialNumber: r,
            ...(isRefresh ? { getDeviceDetails: true } : { rebootDevice: true }),
          }),
        ),
      );
    } finally {
      setUpdating(false);
    }

    if (!isRefresh) {
      return;
    }

    if (isMountedRef.current) {
      showInfo("Refresh command sent. Waiting for devices to report new values...");
    }

    setIsRefreshPolling(true);

    let isAllRefreshed = false;
    try {
      isAllRefreshed = await pollUntil(
        async () => {
          // stop polling after unmount
          if (!isMountedRef.current) {
            return true;
          }

          const fresh: any = await mutate?.();
          const freshBySerial = new Map<string | undefined, string>(
            (fresh?.deviceData ?? []).map((d: DeviceValueTypes): [string | undefined, string] => [
              d.eiSerialNumber,
              d.lastCheckIn,
            ]),
          );
          return rows.every((eiSerialNumber) => {
            const lastCheckIn = freshBySerial.get(eiSerialNumber);
            return lastCheckIn !== undefined && lastCheckIn !== lastCheckInBySerial.get(eiSerialNumber);
          });
        },
        REFRESH_VALUES_POLL_INTERVAL,
        REFRESH_VALUES_POLL_TIMEOUT,
      );
    } finally {
      setIsRefreshPolling(false);
    }

    if (!isMountedRef.current) {
      return;
    }

    if (isAllRefreshed) {
      showSuccess("Device values refreshed.");
    } else {
      showWarning("Some devices have not reported new values yet. Values will update when they check in.");
    }
  };

  const handleLocationTrail = async (isDisable: boolean) => {
    handleActionsClose();
    const isConfirmed = await getConfirmation({
      title: "Location Tracking",
      content: `Are you sure you want to ${isDisable ? "disable" : "enable"} location tracking`,
      buttonTxt: `${isDisable ? ButtonText.Disable : ButtonText.Enable} `,
    });

    if (!isConfirmed) {
      return;
    }

    setUpdating(true);

    const formData = isDisable ? { disableLocationTrail: true } : { enableLocationTrail: true };

    const requests = selectedRows.map((r) =>
      changeDeviceStatusFromUI({
        eiSerialNumber: r,
        ...formData,
      }),
    );
    try {
      await Promise.all(requests);
      fetchData();
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateDeviceSoftware = () => {
    handleActionsClose();

    // Filter selected devices to only those with updateAvailable: true
    const devicesWithUpdates = deviceData.filter(
      (device: DeviceValueTypes) =>
        device.eiSerialNumber && selectedRows.includes(device.eiSerialNumber) && device.updateAvailable === true,
    );

    if (devicesWithUpdates.length === 0) {
      showWarning("No selected devices have updates available.");
      return;
    }

    setIsOpenUpdateDialog(true);
  };

  const handleConfirmUpdate = async (updateNow: boolean, updateTime: string) => {
    // Filter selected devices to only those with updateAvailable: true
    const devicesWithUpdates = deviceData.filter(
      (device: DeviceValueTypes) =>
        device.eiSerialNumber && selectedRows.includes(device.eiSerialNumber) && device.updateAvailable === true,
    );

    const serialNumbers = devicesWithUpdates
      .map((device: DeviceValueTypes) => device.eiSerialNumber)
      .filter(Boolean) as string[];

    if (serialNumbers.length === 0) {
      showWarning("No devices with updates available.");
      setIsOpenUpdateDialog(false);
      return;
    }

    setIsUpdatingSoftware(true);

    try {
      const response = await bulkUpdateDeviceSoftware({
        serialNumbers,
        updateNow,
        updateTime,
      });

      if (response?.data === "Success") {
        showSuccess(
          `Software update ${updateNow ? "initiated" : "scheduled"} successfully for ${
            serialNumbers.length
          } device(s).`,
        );
        setIsOpenUpdateDialog(false);
        setSelectedRows([]);
        fetchData();
      } else if (response?.data?.includes("Rejected")) {
        showError(response.data);
        setIsOpenUpdateDialog(false);
      } else {
        showSuccess(`Software update ${updateNow ? "initiated" : "scheduled"} successfully.`);
        setIsOpenUpdateDialog(false);
        setSelectedRows([]);
        fetchData();
      }
    } catch (error: any) {
      reportError("devices.handleConfirmUpdate", error);
      const errorMessage = error?.response?.data || error?.message || "Failed to update device software.";
      showError(errorMessage);
    } finally {
      setIsUpdatingSoftware(false);
    }
  };

  const getMegaFi2SerialNumbers = (): string[] => {
    return deviceData
      .filter(
        (device: DeviceValueTypes) =>
          device.eiSerialNumber &&
          selectedRows.includes(device.eiSerialNumber) &&
          device.eiSoftwareVersion?.startsWith("MegaFi_2"),
      )
      .map((device: DeviceValueTypes) => device.eiSerialNumber)
      .filter(Boolean) as string[];
  };

  const handlePushDeviceConfig = () => {
    handleActionsClose();

    const megaFi2Devices = getMegaFi2SerialNumbers();

    if (megaFi2Devices.length === 0) {
      showWarning("Configuration push is only supported on MegaFi 2 devices.");
      return;
    }

    setIsOpenConfigDialog(true);
  };

  const handleConfirmConfigPush = async (
    configFileBase64: string,
    configFileName: string,
    applyNow: boolean,
    scheduleTime: string,
  ) => {
    const serialNumbers = getMegaFi2SerialNumbers();

    if (serialNumbers.length === 0) {
      showWarning("No MegaFi 2 devices selected.");
      setIsOpenConfigDialog(false);
      return;
    }

    setIsPushingConfig(true);

    try {
      const response = await bulkPushDeviceConfig({
        serialNumbers,
        configFileBase64,
        configFileName,
        applyNow,
        scheduleTime,
      });

      if (response?.data?.includes?.("Rejected")) {
        showError(response.data);
        setIsOpenConfigDialog(false);
      } else {
        showSuccess(
          `Configuration push ${applyNow ? "initiated" : "scheduled"} for ${serialNumbers.length} device(s).`,
        );
        setIsOpenConfigDialog(false);
        setSelectedRows([]);
        fetchData();
      }
    } catch (error: any) {
      reportError("devices.handleConfirmConfigPush", error);
      const errorMessage = error?.response?.data || error?.message || "Failed to push device configuration.";
      showError(errorMessage);
    } finally {
      setIsPushingConfig(false);
    }
  };

  const goToMultipleDevicesPage = () => {
    navigate("/add-multiple-devices");
  };

  const getCheckBox = (isItemSelected: boolean | undefined, row: DeviceValueTypes) => {
    if (isReadOnly || isDemoUser) {
      return <TableBodyCell size="medium" />;
    }

    return (
      <TableBodyCell padding="checkbox">
        <Checkbox color="primary" checked={isItemSelected} onChange={(event) => handleChange(event, row)} />
      </TableBodyCell>
    );
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <PageTitle>Devices</PageTitle>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {isDemoUser && <BoldText>Demo Account Expires: {expirationDate}</BoldText>}
          <NxtIconButton
            icon={<OpenInNewIcon />}
            text="Release Notes"
            onClick={() => window.open(RELEASE_NOTES_URL, "_blank", "noopener,noreferrer")}
          />
        </div>
      </div>

      <div className={style.barContainer}>
        <div className={style.filterContainer}>
          <TableFilterNew columns={filterColumns} onSearch={onSearch} onReset={onResetFilter} />
        </div>
        {isReadOnly || isDemoUser ? (
          <div />
        ) : (
          <ActionBar className={style.buttonOptions}>
            <Box display="flex">
              <NxtIconButton icon={<AddCircleOutlined />} onClick={onNewDevice} text="New Device" />
              <NxtIconButton icon={exportFileIcon} onClick={getDeviceDataCsv} text="Export Csv" />
              <NxtIconButton
                icon={<DeleteIcon />}
                disabled={selectedRows?.length <= 0}
                onClick={onRemoveDevice}
                text="Remove Device"
              />

              <NxtIconButton
                id="device-actions-button"
                disabled={selectedRows?.length <= 0}
                aria-controls={open ? "device-actions-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                icon={<MoreVertIcon />}
                onClick={handleActionsClick}
                text="Options"
              />

              <Menu
                id="device-actions-menu"
                aria-labelledby="device-actions-button"
                anchorEl={actionsAnchorEl}
                open={open}
                onClose={handleActionsClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <MenuItem onClick={() => handleLocationTrail(true)}>Disable Location Tracking</MenuItem>
                <MenuItem onClick={() => handleLocationTrail(false)}>Enable Location Tracking</MenuItem>
                <MenuItem onClick={() => handleUpdateDevice(true)} disabled={isRefreshPolling}>
                  Refresh Values
                </MenuItem>
                <MenuItem onClick={() => handleUpdateDevice(false)}>Reboot Device</MenuItem>
                <MenuItem onClick={handleUpdateDeviceSoftware}>Update Device Software</MenuItem>
                <MenuItem onClick={handlePushDeviceConfig}>Push Device Configuration</MenuItem>
              </Menu>
            </Box>
          </ActionBar>
        )}
      </div>

      <CustomTable
        boxShadow
        rowsPerPageOptions={[25, 50, 100]}
        totalCount={totalDeviceCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(pageNum) => {
          setPage(pageNum);
          setSelectedRows([]);
        }}
        onRowsPerPageChange={(rowPerPage) => {
          setPage(0);
          setRowsPerPage(rowPerPage);
          setSelectedRows([]);
        }}
      >
        <CustomTable.Header
          columns={isReadOnly ? READ_ONLY_DASHBOARD_COLUMNS : DASHBOARD_COLUMNS}
          onRequestSort={handleRequestSort}
          onSelectAll={handleSelectAll}
          isAllSelected={isAllSelected}
          hasCheckbox={!isReadOnly && !isDemoUser}
          isIndeterminate={isIndeterminate}
          defaultOrder="asc"
          defaultOrderBy="eiSerialNumber"
        />

        <CustomTable.Body isLoading={isLoading || updating} deviceData={deviceData}>
          {!isLoading &&
            deviceData &&
            deviceData.map((row: DeviceValueTypes) => {
              const { eiSerialNumber } = row;
              const isItemSelected = isSelectedRow(eiSerialNumber);

              return (
                row && (
                  <TableRow hover role="checkbox" tabIndex={-1} key={eiSerialNumber} selected={isItemSelected}>
                    {getCheckBox(isItemSelected, row)}
                    {dataColumns.map((column: DeviceColumnTypes) => {
                      const value = row[column.id as keyof DeviceValueTypes];

                      return (
                        <TableBodyCell
                          sx={!isReadOnly ? { cursor: "pointer" } : { cursor: "default" }}
                          size={column.type === COLUMN_TYPE.OPTION ? "small" : "medium"}
                          className={column.className}
                          key={column.id}
                          align={column.align}
                          onClick={() => handleEIRowClick(getRowSN(row))}
                        >
                          {getTableCellValue(column, value, row)}
                        </TableBodyCell>
                      );
                    })}
                  </TableRow>
                )
              );
            })}
        </CustomTable.Body>
      </CustomTable>

      <Popup
        open={isOpenNewDeviceModal}
        isNewDevice
        onHeaderClick={goToMultipleDevicesPage}
        onClose={onModalClose}
        disableEscapeKeyDown
        title="New Device"
        primaryBtn={{ text: "Save", onClick: handleSubmit(onAddNewDevice) }}
        secondaryBtn={{ text: "Cancel", onClick: () => setIsOpenNewDeviceModal(false) }}
      >
        {" "}
        <div className={style.popupContentContainer}>
          <StyledTextField
            onKeyDown={(event: KeyboardEvent) => onEnter(event, handleSubmit(onAddNewDevice))}
            label="Serial Number"
            {...register("serialNumber", { required: "Serial Number is required", minLength: 11, maxLength: 15 })}
            error={errors.serialNumber}
            style={{ marginTop: 16 }}
          />
          {errors.serialNumber && <FieldError>{errors.serialNumber.message}</FieldError>}
          {errors.serialNumber &&
            (errors.serialNumber.type === "maxLength" || errors.serialNumber.type === "minLength") && (
              <FieldError>Serial Number should be 11, 12, or 15 characters in length</FieldError>
            )}

          <StyledTextField
            style={{ paddingBottom: "32px" }}
            onKeyDown={(event: KeyboardEvent) => onEnter(event, handleSubmit(onAddNewDevice))}
            label="MAC Address"
            {...register("macAddress", { required: "MAC Address is required" })}
            error={errors.macAddress}
          />
          {errors.macAddress && <FieldError>{errors.macAddress.message}</FieldError>}
          <CommonSelect
            label="Pool"
            name="devicePool"
            options={poolOptions}
            control={control}
            rules={{ required: "Device Pool is required" }}
            error={errors.devicePool}
          />
          {errors.devicePool && <FieldError>{errors.devicePool.message}</FieldError>}
        </div>
      </Popup>

      <Popup title={modalMessage} open={isCreatingNewDevice}>
        <Spinner />
      </Popup>

      <UpdateSoftwareDialog
        open={isOpenUpdateDialog}
        onClose={() => setIsOpenUpdateDialog(false)}
        deviceCount={
          deviceData.filter(
            (device: DeviceValueTypes) =>
              device.eiSerialNumber && selectedRows.includes(device.eiSerialNumber) && device.updateAvailable === true,
          ).length
        }
        onConfirm={handleConfirmUpdate}
      />

      <PushConfigDialog
        open={isOpenConfigDialog}
        onClose={() => setIsOpenConfigDialog(false)}
        deviceCount={getMegaFi2SerialNumbers().length}
        isSubmitting={isPushingConfig}
        onConfirm={handleConfirmConfigPush}
      />

      <Confirmation />
    </>
  );
}
